import * as _ from 'lodash';
import { useMemo } from 'react';
import { Path, useFormContext, useWatch } from 'react-hook-form';

import { CommitmentMechanicsFormData } from '../types';
import { usePreviousAndCurrentCommitmentFormData } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/commitment-mechanics/[commitmentId]/hooks';
import { computeDiff } from '~/core/routes/settings/implementations/common/DiffInputs/hooks';

export const useValue = <TName extends Path<CommitmentMechanicsFormData>>(
  name: TName,
) => {
  return useWatch<CommitmentMechanicsFormData, TName>({ name });
};

export const useTotalProRataAmount = (): number => {
  const lenders = useValue('wipForm.lenders');
  const total = lenders.reduce((p, c) => p + (c.proRataAmount ?? 0), 0);
  return total;
};

export const useIsOverTotalCommitment = (): boolean => {
  const totalProRataAmount = useTotalProRataAmount();
  const totalCommitmentAmount = useValue('wipForm.totalCommitment');

  if (totalCommitmentAmount === null) {
    return false;
  }

  return totalProRataAmount > totalCommitmentAmount;
};

export const useLenderDiffResult = (lenderId: string | undefined) => {
  const form = useFormContext<CommitmentMechanicsFormData>();
  const commitmentKey = form.getValues('wipForm.key');

  const {
    commitmentFormData,
    previousCommitmentFormData,
  } = usePreviousAndCurrentCommitmentFormData(commitmentKey);

  const currentLender = commitmentFormData?.lenders?.find(
    (l) => l?.lenderId === lenderId,
  );

  const previousLender = previousCommitmentFormData?.lenders?.find(
    (l) => l?.lenderId === lenderId,
  );

  const diffResult = computeDiff(
    currentLender,
    previousLender,
    _.isEqual,
    (lender) => String(lender.proRataAmount),
  );

  return useMemo(() => ({ diffResult, currentLender, previousLender }), [
    diffResult,
    currentLender,
    previousLender,
  ]);
};
